import { cn } from '@/lib/utils'
import { formatChatTimestamp } from '@/lib/chatFormat'
import type { ActionCardData, AgentChatMessage } from '@/lib/chatTypes'
import { MarkdownContent } from '@/components/agents/MarkdownContent'
import { ToolCallIndicator, getToolLabel } from '@/components/agents/ToolCallIndicator'
import { ActionCard } from '@/components/agents/ActionCard'
import { Button } from '@/components/ui/button'
import { extractNavigateLinks } from '@/lib/chatMapping'

export type AgentMessageProps = {
  message: AgentChatMessage
  onActionPrimary?: (card: ActionCardData) => void
  onActionSecondary?: (card: ActionCardData) => void
  // CTAs des ActionCards désactivés (ex. pendant un stream).
  actionsDisabled?: boolean
  // Story 7.1 (PO Q3) — lien d'une carte exécutée (`card.navigate`).
  onNavigate?: (route: string) => void
  className?: string
}

export function AgentMarker({ streaming }: { streaming?: boolean }) {
  return (
    <div className="flex items-center gap-2 text-xs font-medium text-slate-600">
      <span
        aria-hidden="true"
        className={cn(
          'h-2 w-2 rounded-full bg-gradient-to-r from-rose-500 to-violet-500',
          streaming && 'motion-safe:animate-pulse'
        )}
      />
      <span>Agent SustainTrack</span>
    </div>
  )
}

function ThinkingDots() {
  return (
    <span className="inline-flex items-center gap-1 text-sm text-slate-500">
      Agent réfléchit
      <span aria-hidden="true" className="inline-flex gap-0.5">
        <span className="h-1 w-1 rounded-full bg-slate-400 motion-safe:animate-bounce [animation-delay:-0.3s]" />
        <span className="h-1 w-1 rounded-full bg-slate-400 motion-safe:animate-bounce [animation-delay:-0.15s]" />
        <span className="h-1 w-1 rounded-full bg-slate-400 motion-safe:animate-bounce" />
      </span>
      <span className="sr-only">…</span>
    </span>
  )
}

export function AgentMessage({
  message,
  onActionPrimary,
  onActionSecondary,
  actionsDisabled,
  onNavigate,
  className,
}: AgentMessageProps) {
  const isStreaming = message.status === 'streaming'
  const tools = message.toolInvocations ?? []
  const cards = message.actionCards ?? []
  const isThinking = isStreaming && !message.content && tools.length === 0
  const time = formatChatTimestamp(message.createdAt)
  // Story 7.3 : liens Tier 1 (bouton par route), uniquement une fois la réponse terminée.
  const navLinks = !isStreaming && onNavigate ? extractNavigateLinks(message.toolInvocations) : []

  return (
    <div className={cn('flex flex-col items-start gap-1.5', className)} data-status={message.status}>
      <AgentMarker streaming={isStreaming} />
      <div className="w-full max-w-[90%] space-y-2 rounded-2xl rounded-tl-sm border border-slate-100 bg-[var(--chat-assistant-bg)] px-4 py-3 shadow-sm">
        {tools.length > 0 ? (
          <div className="space-y-1">
            {tools.map((t) => (
              <ToolCallIndicator
                key={t.id}
                name={t.name}
                label={getToolLabel(t.name, t.args)}
                status={t.status}
                errorMessage={t.errorMessage}
                startedAt={t.startedAt}
              />
            ))}
          </div>
        ) : null}

        {isThinking ? <ThinkingDots /> : null}

        {message.content ? (
          <div className="relative">
            <MarkdownContent content={message.content} />
            {isStreaming ? (
              <span
                aria-hidden="true"
                data-testid="streaming-cursor"
                className="ml-0.5 inline-block h-4 w-[2px] translate-y-0.5 bg-[var(--chat-streaming-cursor)] motion-safe:animate-pulse"
              />
            ) : null}
          </div>
        ) : null}

        {cards.map((card) => {
          // Story 7.1 : CTA uniquement sur une carte `proposed` ; pendant la
          // fenêtre d'annulation (`confirmed` + compte à rebours), seul
          // « Annuler (n s) » reste cliquable, même pendant un nouveau stream.
          const counting = card.state === 'confirmed' && card.undoSecondsLeft !== undefined
          const actionable = !card.readOnly && card.state === 'proposed'
          const nav = card.navigate
          return (
            <ActionCard
              key={card.id}
              tier={card.tier}
              title={card.title}
              description={card.description}
              state={card.state}
              resultText={card.resultText}
              errorText={card.errorText}
              primaryLabel={card.primaryLabel}
              preview={card.preview}
              resultLink={
                nav && onNavigate ? { label: nav.label, onClick: () => onNavigate(nav.route) } : undefined
              }
              secondaryLabel={counting ? `Annuler (${card.undoSecondsLeft} s)` : undefined}
              onPrimary={actionable && onActionPrimary ? () => onActionPrimary(card) : undefined}
              onSecondary={
                (actionable || (counting && !card.readOnly)) && onActionSecondary
                  ? () => onActionSecondary(card)
                  : undefined
              }
              actionsDisabled={actionable ? actionsDisabled : false}
            />
          )
        })}

        {navLinks.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {navLinks.map((link) => (
              <Button
                key={link.route}
                size="sm"
                variant="outline"
                aria-label={link.label}
                onClick={() => onNavigate?.(link.route)}
                className="focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring"
              >
                {link.label} →
              </Button>
            ))}
          </div>
        ) : null}

        {message.status === 'error' && message.errorMessage ? (
          <p role="status" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {message.errorMessage}
          </p>
        ) : null}
      </div>
      {time && !isStreaming ? (
        <time dateTime={message.createdAt} className="text-[11px] text-slate-500">
          {time}
        </time>
      ) : null}
    </div>
  )
}
